import { rtkOrgClient } from '@gm-commercial/rtk-clients';
import type { OrganizationResponseV20241120 } from '@gm-commercial/rtk-clients/organizations/types';
import { createContext, type ReactNode, useContext } from 'react';
import { useSelector } from 'react-redux';

import { useOrgId } from '~/common/hooks/useOrgId';
import { ActivationStatus } from '~/common/models/common.model';
import { VerificationStatus } from '~/common/models/organization.model';
import { selectProfileStatus } from '~/store/profile/profileSlice.selectors';

export type OrganizationDetailsContextValue = {
  /** The current organization; `undefined` until the request resolves. */
  organization?: OrganizationResponseV20241120;
  /** True only once the organization resolves to `VERIFIED`. */
  isStatusVerified: boolean;
  /** True while the organization request is in flight. */
  isLoading: boolean;
  isError: boolean;
  /** Refetches organization details for the organization in context. */
  refetch: () => void;
};

const OrganizationDetailsContext =
  createContext<OrganizationDetailsContextValue>({
    organization: undefined,
    isStatusVerified: false,
    isLoading: false,
    isError: false,
    refetch: () => {},
  });

/**
 * Fetches the current organization and shares it, plus the derived
 * `isStatusVerified` flag, with everything rendered under a secure route so
 * views and their dialogs read the same values instead of each issuing their
 * own request.
 *
 * It is mounted per secure route (see `AppContent`) because `useOrgId` resolves `:organizationsId`
 * through `useParams`, which is only populated inside the matched route.
 *
 * The request is skipped when there is no organization in context (internal and
 * advisor users) or while the profile is not yet active (a pending user still on
 * terms and conditions is not authorized for this call), which leaves consumers
 * with `isStatusVerified: false`.
 */
export const OrganizationDetailsProvider = ({
  children,
}: {
  children?: ReactNode;
}): JSX.Element => {
  const organizationsId = useOrgId();
  const isProfileActive =
    useSelector(selectProfileStatus) === ActivationStatus.ACTIVE;
  const canRequestOrganization = Boolean(organizationsId) && isProfileActive;

  const { currentData, isFetching, isLoading, isError, refetch } =
    rtkOrgClient.useGetOrgByIdQuery(
      { id: organizationsId },
      { skip: !canRequestOrganization }
    );

  const organizationResult = currentData?.result;
  const organization =
    organizationResult?.id === organizationsId ? organizationResult : undefined;

  const value = {
    organization,
    isStatusVerified:
      organization?.verificationStatus === VerificationStatus.VERIFIED,
    isLoading:
      canRequestOrganization &&
      organization === undefined &&
      (isLoading || isFetching),
    isError,
    refetch: () => {
      void refetch();
    },
  };

  return (
    <OrganizationDetailsContext.Provider value={value}>
      {children}
    </OrganizationDetailsContext.Provider>
  );
};

/**
 * Reads the current organization and its verification state.
 *
 * Falls back to an unresolved value (no organization, not verified, not
 * loading) when no provider is mounted, so consumers that gate on
 * `isStatusVerified` stay hidden rather than crashing.
 * @returns The current organization details.
 */
export const useOrganizationDetails = (): OrganizationDetailsContextValue =>
  useContext(OrganizationDetailsContext);
